import { useEffect, useRef, useState } from 'react';

import { CommandCentrePosition } from '@/types';

import {
  applyTrackingDecay,
  DEFAULT_TRACKING_THRESHOLDS,
  TrackingThresholds,
} from '../utils/operatorTracking';

const EMPTY_POSITIONS: CommandCentrePosition[] = [];

export function useOperatorDecayTimer(
  positions: CommandCentrePosition[] | undefined,
  thresholds: TrackingThresholds = DEFAULT_TRACKING_THRESHOLDS,
): CommandCentrePosition[] {
  const stablePositions = positions ?? EMPTY_POSITIONS;
  const [decayed, setDecayed] = useState(stablePositions);
  const baseRef = useRef(stablePositions);
  const tickRef = useRef(0);
  const thresholdsRef = useRef(thresholds);
  thresholdsRef.current = thresholds;

  useEffect(() => {
    baseRef.current = stablePositions;
    tickRef.current = 0;
    setDecayed((prev) => {
      // Avoid setState when both are the shared empty constant / same reference.
      if (prev === stablePositions) return prev;
      if (prev.length === 0 && stablePositions.length === 0) return prev;
      return stablePositions;
    });
  }, [stablePositions]);

  useEffect(() => {
    const tickMs = thresholds.decayTickMs;
    const interval = setInterval(() => {
      tickRef.current += thresholdsRef.current.decayTickMs / 1000;
      setDecayed(
        applyTrackingDecay(
          baseRef.current,
          tickRef.current,
          thresholdsRef.current,
        ),
      );
    }, tickMs);

    return () => clearInterval(interval);
  }, [thresholds.decayTickMs]);

  return decayed;
}
